import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../dialog';
import { Button } from '../../../button/button';
import LoadingSpinner from '../../../loading-spinner';
import { useState } from 'react';
import clsx from 'clsx';
import { Trash } from 'lucide-react';
import { supabase } from '../../../../../lib/supabase';
import { DeleteAllConversationsButtonProps } from './types';
import { fetchWorkspaceProfilesForUser } from '../../../../../lib/utils/fetch-workspace-profiles-for-user';
import { useSimulations } from '../../../../../contexts/SimulationContext';
import { useSimulationStore } from '../../../../../stores/simulationStore';
import conversationSimulationData from '../../../../../data/conversationSimulation/data.json';

export function DeleteAllConversationsButton({
  setSelectedAgent,
  simulationsLength,
}: DeleteAllConversationsButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const { deleteAllSimulations } = useSimulations();
  const { resetSimulation } = useSimulationStore();

  const handleDelete = async () => {
    setError(null);
    setIsLoading(true);

    try {
      await deleteAllSimulations();

      setSelectedAgent(null);
      resetSimulation();

      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          fetchWorkspaceProfilesForUser();
        }
      });

      setOpen(false);
    } catch (err) {
      console.error('Error deleting all conversations:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to delete all conversations. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="link"
          className="h-fit p-0 text-xs text-prompt-safecomoutrageous-orange"
          tooltipContent={conversationSimulationData?.deleteAllButton?.tooltip}
        >
          <Trash />
          Delete all ({simulationsLength})
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete all conversations</DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete all
            conversations.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 flex flex-col gap-2">
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            onClick={handleDelete}
            disabled={isLoading}
            variant="outline"
            className={clsx(
              'text-red-600 hover:text-red-700',
              isLoading && 'px-11',
            )}
          >
            {isLoading ? (
              <LoadingSpinner strokeWidth={7} />
            ) : (
              <>
                <Trash />
                Delete
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
